import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { reportService, adminService, emailService } from '../../services/api';
import { formatClockTime } from '../../hooks/useEmailBatch';
import Card from '../../components/common/Card';
import { FiPieChart, FiList, FiAlertCircle, FiCalendar } from 'react-icons/fi';
import ReportSummary from '../../components/admin/ReportSummary';
import ReportCharts from '../../components/admin/ReportCharts';
import DetailedReport from '../../components/admin/DetailedReport';
import WeeklyHoursView from '../../components/admin/WeeklyHoursView';
import Button from '../../components/common/Button';

const PageContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Tab = styled.button`
  background: ${({ active, theme }) => active ? theme.colors.primary : 'transparent'};
  color: ${({ active, theme }) => active ? 'white' : theme.colors.textSecondary};
  border: 1px solid ${({ active, theme }) => active ? theme.colors.primary : theme.colors.border};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  
  &:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
  
  &:hover {
    background: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.backgroundLight};
  }
`;

const AlertMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  ${({ type, theme }) => {
    if (type === 'success') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (type === 'error') {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SuccessState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.success};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const MissingControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const DateInput = styled.input`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  min-height: 44px;
`;

const MissingSummary = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: top;
`;

const CustomerGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const CustomerHeading = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
  padding-bottom: ${({ theme }) => theme.spacing[1]};
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
`;

const DayChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const DayChip = styled.span`
  display: inline-block;
  background: ${({ theme }) => theme.colors.warning}22;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.warning}55;
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  padding: 1px 6px;
  font-size: 0.72rem;
`;

const ReminderNote = styled.div`
  font-size: 0.75rem;
  margin-top: 4px;
  color: ${({ ok, theme }) => (ok ? theme.colors.success : theme.colors.error)};
`;

const toDateStr = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
// Default window for the missing-"Finished" report: 1st of this month → today.
const monthStartDate = () => { const d = new Date(); return toDateStr(new Date(d.getFullYear(), d.getMonth(), 1)); };
// The urgent reminder is queued behind the mail limit (v1.42.0). When it does
// not go out at once, the row note follows its outbox batch until the
// employee's mail settles — 3 s apart, for at most 10 minutes.
const REMINDER_POLL_MS = 3000;
const REMINDER_POLL_MAX_MS = 10 * 60 * 1000;

const TimeReports = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [alert, setAlert] = useState(null);

  // Missing "Finished" report state (REQ #9): day-level, grouped by customer.
  const [missStart, setMissStart] = useState(monthStartDate());
  const [missEnd, setMissEnd] = useState(toDateStr(new Date()));
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingData, setMissingData] = useState(null);
  const [missingError, setMissingError] = useState(null);
  // Per-employee reminder progress/result, keyed by employeeId.
  const [reminding, setReminding] = useState({});
  const [reminderMsg, setReminderMsg] = useState({});
  // Active reminder-batch polls, keyed by employeeId: { token, timer }.
  const reminderPolls = useRef({});

  useEffect(() => {
    fetchReportData();
  }, []);

  // Stop following reminder batches: on re-fetch (the rows are replaced) and
  // on unmount. A poll that is mid-flight finds its token gone and does nothing.
  const stopReminderPolls = () => {
    Object.values(reminderPolls.current).forEach((poll) => clearTimeout(poll.timer));
    reminderPolls.current = {};
  };
  useEffect(() => () => stopReminderPolls(), []);

  const watchReminderBatch = (employeeId, batchId) => {
    const token = {};
    const startedAt = Date.now();
    const stillMine = () => {
      const entry = reminderPolls.current[employeeId];
      return Boolean(entry) && entry.token === token;
    };
    const note = (msg) => setReminderMsg((s) => ({ ...s, [employeeId]: msg }));

    function schedule() {
      if (Date.now() - startedAt >= REMINDER_POLL_MAX_MS) {
        delete reminderPolls.current[employeeId];
        return;
      }
      reminderPolls.current[employeeId] = { token, timer: setTimeout(poll, REMINDER_POLL_MS) };
    }

    async function poll() {
      if (!stillMine()) return;
      try {
        const batch = await emailService.getBatch(batchId);
        if (!stillMine()) return;
        // The batch also carries the office copies; only the employee's own
        // mail decides what the row says.
        const row = (batch.items || []).find((item) => item.kind === 'finish_reminder');
        if (row && row.status === 'sent') {
          note({ ok: true, text: `Sent ${formatClockTime(row.sentAt)}` });
          delete reminderPolls.current[employeeId];
          return;
        }
        if (row && (row.status === 'failed' || row.status === 'cancelled')) {
          note({
            ok: false,
            text: row.status === 'cancelled' ? 'Cancelled' : `Failed: ${row.lastError || 'the mail server rejected it'}`,
          });
          delete reminderPolls.current[employeeId];
          return;
        }
        if (row && row.status === 'queued' && row.attempts > 0 && row.lastError) {
          // Backed off after a failed attempt (server unreachable, login
          // refused): say so while we keep watching, instead of nothing.
          note({ ok: false, text: `Queued for retry (attempt ${row.attempts}/${row.maxAttempts}): ${row.lastError}` });
        }
      } catch (error) {
        // Transient — keep polling within the budget.
      }
      schedule();
    }

    schedule();
  };

  const fetchMissingHours = async () => {
    try {
      setMissingLoading(true);
      setMissingError(null);
      setReminderMsg({});
      stopReminderPolls();
      const data = await adminService.getMissingFinishedReport(missStart || undefined, missEnd || undefined);
      setMissingData(data);
    } catch (error) {
      console.error('Error fetching missing hours:', error);
      setMissingError('Failed to load the report. Please try again.');
    } finally {
      setMissingLoading(false);
    }
  };

  const sendReminder = async (employeeId) => {
    setReminding((s) => ({ ...s, [employeeId]: true }));
    setReminderMsg((s) => ({ ...s, [employeeId]: null }));
    try {
      const res = await adminService.remindEmployeeFinished(employeeId, missStart || undefined, missEnd || undefined);
      const d = (res && res.data) || {};
      // 'deferred' = queued for retry after a mail server error, not a success.
      setReminderMsg((s) => ({ ...s, [employeeId]: { ok: !d.deferred, text: (res && res.message) || 'Reminder sent' } }));
      if (d.delivery === 'queued' && d.batchId) watchReminderBatch(employeeId, d.batchId);
    } catch (error) {
      setReminderMsg((s) => ({ ...s, [employeeId]: { ok: false, text: error.message || 'Could not send the reminder' } }));
    } finally {
      setReminding((s) => ({ ...s, [employeeId]: false }));
    }
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const data = await reportService.getAdminReports();
      setReportData(data);
    } catch (error) {
      console.error('Error fetching report data:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load report data. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Time Reports & Analytics</PageTitle>
      </PageHeader>
      
      <TabContainer>
        <Tab
          active={activeTab === 'dashboard'}
          onClick={() => setActiveTab('dashboard')}
        >
          <FiPieChart />
          Dashboard
        </Tab>
        <Tab
          active={activeTab === 'detailed'}
          onClick={() => setActiveTab('detailed')}
        >
          <FiList />
          Detailed Reports
        </Tab>
        <Tab
          active={activeTab === 'missing'}
          onClick={() => setActiveTab('missing')}
        >
          <FiAlertCircle />
          Missing Hours
        </Tab>
        <Tab
          active={activeTab === 'weekly'}
          onClick={() => setActiveTab('weekly')}
        >
          <FiCalendar />
          Weekly Hours
        </Tab>
      </TabContainer>
      
      {alert && (
        <AlertMessage type={alert.type}>
          {alert.message}
        </AlertMessage>
      )}
      
      {activeTab === 'dashboard' && (
        <>
          {loading ? (
            <EmptyState>Loading report data...</EmptyState>
          ) : reportData ? (
            <Card padding="md">
              <ReportSummary summary={reportData.summary} />
              <ReportCharts distribution={reportData.distribution} />
            </Card>
          ) : (
            <EmptyState>No report data available</EmptyState>
          )}
        </>
      )}
      
      {activeTab === 'detailed' && (
        <DetailedReport />
      )}

      {activeTab === 'weekly' && (
        <WeeklyHoursView />
      )}

      {activeTab === 'missing' && (
        <Card
          title="Missing hours — days not marked “Finished”"
          subtitle="Active employees, grouped by customer, with each assigned day they have not yet finished. Send an urgent reminder in the employee’s own language."
          padding="md"
        >
          <MissingControls>
            <FieldGroup>
              <FieldLabel htmlFor="miss-start">From</FieldLabel>
              <DateInput id="miss-start" type="date" value={missStart} onChange={(e) => setMissStart(e.target.value)} />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel htmlFor="miss-end">To</FieldLabel>
              <DateInput id="miss-end" type="date" value={missEnd} onChange={(e) => setMissEnd(e.target.value)} />
            </FieldGroup>
            <Button variant="primary" onClick={fetchMissingHours} disabled={missingLoading}>
              {missingLoading ? 'Checking…' : 'Check missing hours'}
            </Button>
          </MissingControls>

          {missingError && <AlertMessage type="error">{missingError}</AlertMessage>}

          {missingLoading ? (
            <EmptyState>Checking who still has unfinished days…</EmptyState>
          ) : missingData ? (
            <>
              <MissingSummary>
                {missingData.start} → {missingData.end}
                {' — '}
                {missingData.totalDays === 0
                  ? 'nothing outstanding'
                  : `${missingData.totalDays} unfinished day${missingData.totalDays === 1 ? '' : 's'} across ${missingData.totalEmployees} employee${missingData.totalEmployees === 1 ? '' : 's'}`}
                {missingData.mailConfigured === false && (
                  <span style={{ color: 'var(--act-danger)' }}> · SMTP not configured — reminders are disabled</span>
                )}
                {missingData.rateLimit && missingData.rateLimit.limit ? (
                  <span> · mail limit {missingData.rateLimit.limit} per {missingData.rateLimit.windowMinutes} min</span>
                ) : null}
              </MissingSummary>
              {missingData.totalDays === 0 ? (
                <SuccessState>Everyone has finished their hours for this period. 🎉</SuccessState>
              ) : (
                missingData.customers.map((cust) => (
                  <CustomerGroup key={cust.customerId}>
                    <CustomerHeading>{cust.customerName}</CustomerHeading>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Employee</Th>
                          <Th style={{ width: 70, textAlign: 'center' }}>Days</Th>
                          <Th>Unfinished days</Th>
                          <Th style={{ width: 200 }}>Reminder</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {cust.employees.map((emp) => {
                          const msg = reminderMsg[emp.employeeId];
                          return (
                            <tr key={emp.employeeId}>
                              <Td>
                                <div style={{ fontWeight: 600 }}>{emp.name}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--ve-ink-600)' }}>{emp.email} · {String(emp.language || 'en').toUpperCase()}</div>
                              </Td>
                              <Td style={{ textAlign: 'center', fontWeight: 700 }}>{emp.count}</Td>
                              <Td>
                                <DayChips>
                                  {emp.days.map((d) => <DayChip key={d}>{d.slice(5)}</DayChip>)}
                                </DayChips>
                              </Td>
                              <Td>
                                <Button
                                  size="sm"
                                  onClick={() => sendReminder(emp.employeeId)}
                                  disabled={reminding[emp.employeeId] || missingData.mailConfigured === false}
                                  style={{ backgroundColor: 'var(--act-danger)', borderColor: 'var(--act-danger)', color: '#fff' }}
                                >
                                  {reminding[emp.employeeId] ? 'Sending…' : 'Send urgent reminder'}
                                </Button>
                                {msg && <ReminderNote ok={msg.ok}>{msg.text}</ReminderNote>}
                              </Td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </CustomerGroup>
                ))
              )}
            </>
          ) : (
            <EmptyState>
              Pick a period (defaults to this month) and check who has not pressed “Finished”.
            </EmptyState>
          )}
        </Card>
      )}
    </PageContainer>
  );
};

export default TimeReports;